/**
 * Box 5: each channel's share of spend (spec §3.3, ruling R15). A channel
 * with no spend in the window is listed under the donut, never drawn as a 0%
 * slice. Pure.
 *
 * @module services/overview/channelShare
 */
import { groupRows, totalsOf } from './overviewMath';
import type { FactRow } from './overviewTypes';

export interface ChannelSlice { platform: string; spend: number; share: number }
export interface ChannelShare {
  /** Σ spend of the slices. */
  total: number;
  /** Channels with spend > 0, most first. */
  slices: ChannelSlice[];
  /** Connected or present channels with no spend, alphabetical. */
  noSpend: string[];
}

/** `connected`: ad channels the workspace syncs, already narrowed by the channel filter; [] when unknown. */
export const channelShareOf = (rows: readonly FactRow[], connected: readonly string[]): ChannelShare => {
  // "Has it any spend" only: null spend is no spend here, and it is never displayed as 0.
  const spendBy = groupRows(rows, (r) => r.platform).map(([platform, own]) => ({ platform, spend: totalsOf(own).spend ?? 0 }));
  const paying = spendBy.filter((c) => c.spend > 0).sort((a, b) => b.spend - a.spend || a.platform.localeCompare(b.platform));
  const total = paying.reduce((s, c) => s + c.spend, 0);
  const slices = paying.map((c) => ({ ...c, share: c.spend / total }));
  const shown = new Set(slices.map((s) => s.platform));
  const noSpend = [...new Set([...connected, ...spendBy.map((c) => c.platform)])].filter((p) => !shown.has(p)).sort();
  return { total, slices, noSpend };
};
